import cn from 'classnames';
import { forwardRef, useMemo } from 'react';

import { useFieldId, visuallyHidden } from '../../../../utils';
import { extractDataAriaAttributes } from '../../../../utils/extract-data-aria-attributes';
import {
  buildTickmarkMeta,
  defaultValueLabelFormat,
  getSliderStyle,
  getTickmarkClipPaths,
  SliderMarkList,
  SliderNumberInput,
  SliderThumb,
  SliderTickmarks,
  useSlider,
  useSliderMarks,
} from '../internal';

import type { ForwardedRef, ReactElement } from 'react';

import type { SliderProps } from './slider.types';

import './slider.scss';

const getRangeThumbDescription = (index: number): string => (index === 0 ? 'minimum' : 'maximum');

/**
 * Slider allows users to select a value or range from a continuous or discrete set of values
 * by moving a thumb along a track.
 *
 * ### Usage
 *
 * ```tsx
 * import { Slider } from '@bloomreach/react-banana-ui';
 *
 * export default function MySlider() {
 *   return <Slider defaultValue={50} />;
 * }
 * ```
 */
const Slider = forwardRef((props: SliderProps, forwardedRef: ForwardedRef<HTMLSpanElement>): ReactElement => {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'aria-valuetext': ariaValuetext,
    className,
    defaultValue,
    disabled = false,
    disableSwap,
    error = false,
    getAriaLabel,
    getAriaValueText,
    inputProps,
    length,
    marks: marksProp,
    max: maxProp = 100,
    min: minProp = 0,
    minDistance = 0,
    name,
    onChange,
    onChangeCommitted,
    orientation = 'horizontal',
    readOnly = false,
    scale,
    shiftStep,
    size = 'md',
    snapToMarks = false,
    step,
    tabIndex,
    tickmarks,
    track = 'normal',
    value,
    valueLabelDisplay = 'auto',
    valueLabelFormat = defaultValueLabelFormat,
    withNumberInput = false,
  } = props;

  const {
    active,
    axis,
    axisProps,
    customMarks,
    dragging,
    focusedThumbIndex,
    getHiddenInputProps,
    getRootProps,
    getThumbProps,
    getThumbStyle,
    handleNumberInputBlur,
    handleNumberInputChange,
    handleNumberInputKeyDown,
    inputValues,
    marks,
    open,
    range,
    renderValues,
    resolvedStep,
    resolvedTrack,
    trackLeap,
    trackOffset,
    values,
  } = useSlider({
    'aria-labelledby': ariaLabelledby,
    defaultValue,
    disabled,
    disableSwap,
    marks: marksProp,
    max: maxProp,
    min: minProp,
    minDistance,
    name,
    onChange,
    onChangeCommitted,
    orientation,
    readOnly,
    rootRef: forwardedRef,
    scale,
    shiftStep,
    snapToMarks,
    step,
    tabIndex,
    track,
    value,
    withNumberInput,
  });

  // The track segment is positioned and sized via inline CSS using axis-specific properties
  // (e.g. `left` + `width` for horizontal, `bottom` + `height` for vertical).
  const trackStyle = {
    ...axisProps[axis].offset(trackOffset),
    ...axisProps[axis].leap(trackLeap),
  };

  const rootProps = getRootProps();
  const rangeLabelBaseId = useFieldId(undefined, 'rbui-slider-range-label');

  const { hasStaggeredLabels, markLevels, markRenderKeys } = useSliderMarks({
    marks,
    max: maxProp,
    min: minProp,
    orientation,
  });

  // Extract data-* and aria-* attributes to spread on the root element,
  // but exclude ARIA attributes that belong on the hidden <input> elements.
  const rootDataAriaAttributes = useMemo(() => {
    const attrs = extractDataAriaAttributes(props);
    delete attrs['aria-label'];
    delete attrs['aria-labelledby'];
    delete attrs['aria-valuetext'];
    return attrs;
  }, [props]);

  const tickmarkMeta = useMemo(
    () => buildTickmarkMeta(tickmarks ?? 0, customMarks, minProp, maxProp),
    [tickmarks, customMarks, minProp, maxProp],
  );

  const tickmarkClipPaths = useMemo(() => {
    return getTickmarkClipPaths(orientation, resolvedTrack, range, trackOffset, trackLeap);
  }, [orientation, range, resolvedTrack, trackLeap, trackOffset]);

  const rangeLabelIds = useMemo(() => {
    if (!range || getAriaLabel || !ariaLabelledby || !rangeLabelBaseId) {
      return [];
    }

    return [0, 1].map((index) => `${rangeLabelBaseId}-${index}`);
  }, [ariaLabelledby, getAriaLabel, range, rangeLabelBaseId]);

  const getResolvedThumbAriaLabel = (index: number): string | undefined => {
    if (getAriaLabel) {
      return getAriaLabel(index);
    }

    if (range && ariaLabel) {
      return `${ariaLabel} ${getRangeThumbDescription(index)}`;
    }

    return ariaLabel;
  };

  const getResolvedThumbAriaLabelledby = (index: number): string | undefined => {
    if (getAriaLabel) {
      return undefined;
    }

    if (!ariaLabelledby) {
      return undefined;
    }

    return range && rangeLabelIds[index] ? `${ariaLabelledby} ${rangeLabelIds[index]}` : ariaLabelledby;
  };

  const sliderClassName = useMemo(() => {
    return cn(className, 'rbui-slider', `rbui-slider--${orientation}`, {
      'rbui-slider--disabled': disabled,
      'rbui-slider--dragging': dragging,
      'rbui-slider--error': error,
      'rbui-slider--has-tickmarks': !!tickmarks,
      'rbui-slider--range': range,
      'rbui-slider--readonly': readOnly,
      'rbui-slider--sm': size === 'sm',
      'rbui-slider--staggered-labels': hasStaggeredLabels,
      'rbui-slider--track-center': resolvedTrack === 'center',
      'rbui-slider--track-false': resolvedTrack === false,
      'rbui-slider--track-inverted': resolvedTrack === 'inverted',
    });
  }, [
    className,
    disabled,
    dragging,
    error,
    hasStaggeredLabels,
    orientation,
    range,
    readOnly,
    resolvedTrack,
    size,
    tickmarks,
  ]);

  const sliderStyle = useMemo(() => getSliderStyle(length, orientation), [length, orientation]);

  const sliderElement = (
    <span
      {...rootProps}
      {...rootDataAriaAttributes}
      aria-readonly={readOnly || undefined}
      className={sliderClassName}
      style={sliderStyle}
    >
      {rangeLabelIds.map((rangeLabelId, index) => (
        <span id={rangeLabelId} key={rangeLabelId} style={visuallyHidden}>
          {getRangeThumbDescription(index)}
        </span>
      ))}
      {/* Rail: the full-length background track, with optional inactive tickmarks */}
      <span className="rbui-slider__rail">
        {tickmarkMeta.length > 0 && <SliderTickmarks isActiveLayer={false} tickmarkMeta={tickmarkMeta} />}
      </span>
      {/* Track: the colored segment representing the current value */}
      {resolvedTrack !== false && <span className="rbui-slider__track" style={trackStyle} />}
      {/* Active tickmarks layer: a duplicate set of ticks clipped to the track region
          so ticks within the active area use the active color */}
      {tickmarkMeta.length > 0 &&
        resolvedTrack !== false &&
        tickmarkClipPaths.map((clipPath, index) => (
          <span
            className="rbui-slider__tickmarks-active-layer"
            data-segment-index={index}
            // eslint-disable-next-line react/no-array-index-key -- clip segments are derived from track mode and rendered in stable order
            key={index}
            style={{ clipPath }}
          >
            <SliderTickmarks isActiveLayer tickmarkMeta={tickmarkMeta} />
          </span>
        ))}
      <SliderMarkList
        axis={axis}
        axisProps={axisProps}
        markLevels={markLevels}
        markRenderKeys={markRenderKeys}
        marks={marks}
        max={maxProp}
        min={minProp}
        range={range}
        track={resolvedTrack}
        trackLeap={trackLeap}
        trackOffset={trackOffset}
      />
      {renderValues.map((renderValue, index) => (
        <SliderThumb
          active={active}
          ariaLabel={getResolvedThumbAriaLabel(index)}
          ariaLabelledby={getResolvedThumbAriaLabelledby(index)}
          ariaValuetext={ariaValuetext}
          axis={axis}
          axisProps={axisProps}
          focusedThumbIndex={focusedThumbIndex}
          getAriaValueText={getAriaValueText}
          getHiddenInputProps={getHiddenInputProps}
          getThumbProps={getThumbProps}
          getThumbStyle={getThumbStyle}
          index={index}
          // eslint-disable-next-line react/no-array-index-key -- thumb count is fixed (1 or 2) per component lifecycle
          key={index}
          max={maxProp}
          min={minProp}
          open={open}
          readOnly={readOnly}
          scale={scale}
          value={renderValue}
          valueLabelDisplay={valueLabelDisplay}
          valueLabelFormat={valueLabelFormat}
          values={values}
        />
      ))}
    </span>
  );

  if (!withNumberInput) {
    return sliderElement;
  }

  /** Renders a number input for direct value entry at the given thumb index. */
  const renderNumberInput = (inputValue: string, index: number): ReactElement => {
    const resolvedInputProps = typeof inputProps === 'function' ? inputProps(index) : inputProps;
    const resolvedThumbAriaLabel = getResolvedThumbAriaLabel(index);
    const resolvedInputAriaLabel = resolvedInputProps?.['aria-label'];

    const resolvedAriaLabel =
      resolvedInputAriaLabel ?? (resolvedThumbAriaLabel ? `${resolvedThumbAriaLabel} input` : undefined);
    const resolvedAriaLabelledby =
      resolvedInputProps?.['aria-labelledby'] ??
      (resolvedAriaLabel ? undefined : getResolvedThumbAriaLabelledby(index));

    return (
      <SliderNumberInput
        ariaLabel={resolvedAriaLabel}
        ariaLabelledby={resolvedAriaLabelledby}
        disabled={disabled}
        error={error}
        inputProps={resolvedInputProps}
        key={range ? `input-${index}` : 'input'}
        max={maxProp}
        min={minProp}
        onBlur={(event) => handleNumberInputBlur(event, index)}
        onChange={(event) => handleNumberInputChange(event, index)}
        onKeyDown={(event) => handleNumberInputKeyDown(event, index)}
        readOnly={readOnly}
        step={resolvedStep ?? 'any'}
        value={inputValue}
      />
    );
  };

  // Range slider with two thumbs: render inputs on either side of the slider
  if (range && inputValues.length === 2) {
    return (
      <div className="rbui-slider__control">
        {renderNumberInput(inputValues[0], 0)}
        {sliderElement}
        {renderNumberInput(inputValues[1], 1)}
      </div>
    );
  }

  // Single slider: render input(s) after the slider
  return (
    <div className="rbui-slider__control">
      {sliderElement}
      {inputValues.map((inputValue, index) => renderNumberInput(inputValue, index))}
    </div>
  );
});

Slider.displayName = 'Slider';

export default Slider;
